// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {selx} from 'clsx';
import {jsx} from 'panel';

import {InputComponent, InputEl} from 'common/components/input';
import {SvgIconEl} from 'common/components/svg-icon';
import {TooltipEl} from 'common/components/tooltip';
import {SelectEl} from 'common/components/select';
import {RadioButtonEl} from 'common/components/radio-button';
import {ButtonEl} from 'common/components/button';
import {Placement} from 'common/components/tooltip/types';
import {ButtonTheme} from 'common/components/button/constants';
import {Permission} from 'common/report/constants';
import {CheckboxEl} from 'common/components/checkbox';

import type {CreateProjectField} from './index';
import styles from './index.module.styl';

export function template(this: CreateProjectField) {
  const {selectedDataResidency, timezones, selectedTimezone, selectedOrganizationId} = this.state;
  const orgData = this.attr(`orgs`);
  const isSetup = this.attr(`is-setup`);
  const isFyt = this.attr(`is-fyt`);
  const t = this.getContext(`i18n`).t;
  return (
    <div sel={selx(`create-project-field-container`)}>
      <InputEl
        sel={selx(styles.projectNameInput)}
        attrs={{
          'field-id': `project-name`,
          label: t(`settings.project-settings.create.name`),
          placeholder: t(`settings.project-settings.create.name-placeholder`),
          ...(isFyt
            ? {
                'help-text': t(`settings.project-settings.create.name-help`),
              }
            : {}),
        }}
        on={{input: (ev) => this.update({projectName: ev.target.value})}}
        hook={{insert: ({elm}) => requestAnimationFrame(() => (elm as InputComponent).focus())}}
      />

      <div sel={selx(styles.dataResidencyRadioGroup)}>
        <div sel={selx(styles.label, styles.mpInputLabel)}>
          {t(`settings.project-settings.create.store-data`)}
          <div sel={selx(styles.info)}>
            <SvgIconEl sel={selx(styles.infoSvg)} attrs={{icon: `info`}} />
            <TooltipEl attrs={{placement: Placement.Bottom, 'max-width': `300px`}}>
              {t(`settings.project-settings.create.eu-info`)}
            </TooltipEl>
          </div>
        </div>

        <div sel={selx(styles.radioBtn)} on={{click: () => this.update({selectedDataResidency: `US`})}}>
          <RadioButtonEl
            selected={selectedDataResidency === `US`}
            children={`🇺🇸 ${t(`settings.project-settings.create.us-option`)}`}
          />
        </div>
        <div sel={selx(styles.radioBtn)} on={{click: () => this.update({selectedDataResidency: `EU`})}}>
          <RadioButtonEl
            selected={selectedDataResidency === `EU`}
            children={`🇪🇺 ${t(`settings.project-settings.create.eu-option`)}`}
          />
        </div>

        {this.helpers.hasIndiaDatacenterAccess() && (
          <div sel={selx(styles.radioBtn)} on={{click: () => this.update({selectedDataResidency: `IN`})}}>
            <RadioButtonEl
              selected={selectedDataResidency === `IN`}
              children={`🇮🇳 ${t(`settings.project-settings.create.in-option`)}`}
            />
          </div>
        )}
      </div>

      <div sel={selx(styles.timezonesDropdownArea)}>
        <div sel={selx(styles.mpInputLabel)}>{t(`settings.project-settings.create.timezone`)}</div>
        <SelectEl
          sel={selx(styles.timezonesDropdown)}
          attrs={{
            options: timezones.map((timezoneEntry) => timezoneEntry[1]),
            'label-transform': `uppercase`,
            tabindex: 0,
            'selected-option': selectedTimezone ? selectedTimezone[1] : ``,
            'force-visibility': true,
          }}
          on={{
            change: this.helpers.handleChangeSelectedTimezone,
          }}
        />
      </div>

      {this.helpers.hasFeatureFlag() ? (
        <div sel={selx(`b2b-checkbox-area`)}>
          <CheckboxEl
            attrs={{
              checked: this.state.isB2BProject,
            }}
            on={{
              click: () => this.update({isB2BProject: !this.state.isB2BProject}),
            }}
          >
            {t(`settings.project-settings.create.enable-b2b`)}
          </CheckboxEl>
        </div>
      ) : null}

      {orgData && orgData.length > 1 && (
        <div sel={selx(styles.selectOrg)}>
          <div sel={selx(styles.selectOrgLabel)}>{t(`settings.project-settings.create.organization`)}</div>
          <div sel={selx(`mp-chrome-create-project-modal-radio-button-container`)}>
            {orgData.map((org) => {
              const disabled = !this.helpers.orgHasPerm(org, Permission.CreateProject);

              return (
                !disabled && (
                  <div sel={selx(styles.radioBtn)}>
                    <RadioButtonEl
                      selected={selectedOrganizationId === org.id}
                      disabled={disabled}
                      onClick={() => !disabled && this.update({selectedOrganizationId: org.id})}
                      children={org.name}
                    />
                  </div>
                )
              );
            })}
          </div>
        </div>
      )}

      <div style={{textAlign: this.attr(`button-area-align`) || `right`}}>
        {isSetup
          ? null
          : !isFyt && (
              <ButtonEl
                sel={selx(`skip-btn`)}
                attrs={{
                  theme: ButtonTheme.Tertiary,
                  role: `button`,
                  tabindex: 0,
                }}
                on={{click: this.helpers.clickedCancel}}
              >
                {t(`settings.project-settings.create.cancel`)}
              </ButtonEl>
            )}
        <ButtonEl
          sel={selx(`create-btn`)}
          attrs={{
            theme: ButtonTheme.Primary,
            role: `button`,
            tabindex: 0,
            disabled: this.helpers.isSubmitDisabled(),
            icon: this.state.createProjectLoading ? `spinner` : undefined,
          }}
          on={{click: !this.helpers.isSubmitDisabled() && this.helpers.createProject}}
        >
          {t(`settings.project-settings.create.create`)}
        </ButtonEl>
      </div>
    </div>
  );
}
